import PropTypes from "prop-types"
import React from "react"
import classNames from "classnames"
import swal from "sweetalert"
import { notifications } from "@mantine/notifications"

import { Icon } from "quorumdesign"

import { QuorumTheme } from "app/static/frontend/design-constants"

import "app/static/stylus/reusable_components/attachments.styl"
import * as S from "app/static/frontend/components/Attachments/style"

/**
 * Individual Attachment with remove icon
 */
export const Attachment = ({ name, removeAttachment }) => (
    <S.Attachment>
        <Icon icon="paperclip" iconFamily="fas" />
        <span>{name}</span>
        <Icon icon="times" iconFamily="fas" onClick={removeAttachment} dataCy="attachment-delete" />
    </S.Attachment>
)

Attachment.propTypes = {
    name: PropTypes.string.isRequired,
    removeAttachment: PropTypes.func.isRequired,
}

/**
 * An AttachmentButton allows users to upload attachments.
 */
export const AttachmentButton = (props) => {
    // success function
    const success = (responseData) => {
        const newData = JSON.parse(responseData)
        if (props.callback) {
            props.callback()
        }
        if (newData.error === null || newData.error === undefined) {
            if (props.swalProgress) {
                swal({
                    title: "Success!",
                    text: " ",
                    button: false,
                    timer: 1000,
                    icon: "success",
                })
            } else if (props.successCallback) {
                props.successCallback()
            }
            return props.addAttachments(newData)
        }

        // user canceled instead of choosing to upload something
        return []
    }

    /*
     * prepareUpload handles the uploading of files once a user has selected
     * files to upload.
     *
     * Calls the preparingCallback function, if provided; this function
     * is given the new files as input and should return a promise that
     * resolves to whether or not attaching the new files should be
     * allowed to continue
     */
    const prepareUpload = async (event) => {
        const { files } = event.target

        // if there are not files from the input, the user cancelled
        if (!files || !files.length) {
            return
        }

        if (props.swalProgress) {
            swal({
                title: "Submitting...",
                text: " ",
                button: false,
                icon: "info",
            })
        } else if (props.preparingCallback) {
            if (!(await props.preparingCallback(files))) {
                // abort sending if the preparingCallback returns false
                return
            }
        }

        const data = new FormData()
        for (let key = 0; key < files.length; key += 1) {
            const file = files[key]
            data.append(key, file)
        }

        // any additional key-value pairs are added
        if (props.addedFields) {
            Object.entries(props.addedFields).forEach(([key, value]) => {
                data.append(key, value)
            })
        }

        $.ajax({
            data,
            success,
            url: "/outbox/attachment/",
            type: "POST",
            cache: false,
            processData: false,
            contentType: false,
            error(jqXHR, textStatus, errorThrown) {
                if (props.swalProgress) {
                    swal({
                        title: "Failed to Upload File",
                        text: " ",
                        icon: "error",
                    })
                } else {
                    props.errorCallback?.()

                    notifications.show({
                        id: "attachment-error",
                        message: jqXHR.responseJSON?.message || "Failed to upload file.",
                        autoClose: false,
                        icon: (
                            <Icon
                                icon="exclamation-circle"
                                iconFamily="fas"
                                color={QuorumTheme.colors.red[5]}
                                style={{ width: "auto" }}
                            />
                        ),
                        closeButtonProps: { "aria-label": "Dismiss notification" },
                        styles: (theme) => ({
                            root: {
                                alignItems: "center",
                                backgroundColor: theme.colors.gray[9],
                                border: 0,

                                "&&": {
                                    maxWidth: "none",
                                    padding: ".75rem 1rem",
                                },
                            },
                            icon: {
                                width: "auto",
                                height: "auto",
                                backgroundColor: "initial",
                                marginRight: ".5rem",
                            },
                            description: {
                                display: "flex",
                                alignItems: "center",
                                gap: ".5rem",
                                color: "white",
                                fontSize: ".875rem",
                                lineHeight: "normal",
                            },
                            closeButton: {
                                color: theme.colors.violet[2],

                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }),
                    })
                }
            },
        })
    }

    const buttonText = props.buttonText || (props.multiple ? "Attach File(s)" : "Attach File")

    return props.variant === "inline" ? (
        <S.AttachmentButton
            // this label's htmlFor must be identical/unique to its respective input's id
            // props.variantId is used with multiple AB Test variation attachment buttons
            data-cy={props.variantId ? `attach-upload-button-${props.variantId}` : "attach-upload-button"}
            htmlFor={props.variantId ? `attach-upload-${props.variantId}` : "attach-upload"}
        >
            <Icon icon="plus" />
            {buttonText}
            <S.Input
                data-cy={props.variantId ? `attach-upload-input-${props.variantId}` : "attach-upload-input"}
                id={props.variantId ? `attach-upload-${props.variantId}` : "attach-upload"}
                type="file"
                onChange={prepareUpload}
                multiple={props.multiple}
                disabled={props.disabled}
            />
        </S.AttachmentButton>
    ) : (
        <label
            className={classNames("attachment-button", "btn", props.classes, {
                disabled: props.disabled,
            })}
        >
            <i className="fa fa-paperclip" />
            {buttonText}
            <input
                data-cy="attach-upload"
                type="file"
                className="upload"
                onChange={prepareUpload}
                multiple={props.multiple}
                disabled={props.disabled}
            />
        </label>
    )
}
AttachmentButton.propTypes = {
    addedFields: PropTypes.object,
    addAttachments: PropTypes.func,
    buttonText: PropTypes.string,
    classes: PropTypes.string,
    disabled: PropTypes.bool,
    errorCallback: PropTypes.func,
    multiple: PropTypes.bool,
    preparingCallback: PropTypes.func,
    successCallback: PropTypes.func,
    swalProgress: PropTypes.bool,
    callback: PropTypes.func,
    variant: PropTypes.oneOf(["default", "inline"]),
    // ID for a/b test variants—unrelated to above variant prop for style
    variantId: PropTypes.number,
}

AttachmentButton.defaultProps = {
    addAttachments: () => undefined,
    addedFields: undefined,
    classes: "btn-primary",
    multiple: true,
    disabled: false,
    swalProgress: true,
    variant: "default",
}

export default AttachmentButton
